/* eslint camelcase: "off" */
/**
 * Session detail — one flow session, end-to-end.
 *
 * Wired to verificationClient.getFlowSession (django-monolith
 * /verification/admin/flow-sessions/<id>/).
 *
 * Layout:
 *  * Header: employee, timestamp, duration, outcome
 *  * Consent record
 *  * Document panels stacked full-width — a flow session is one Meta
 *    Flow (selfie XOR ID capture), so only the relevant section is
 *    rendered; empty halves are skipped.
 *  * Fraud signals per panel
 *  * Decision panel for reviewer verdict (write endpoint pending)
 *  * Image placeholders gated by X-Access-Reason prompt
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Container } from 'reactstrap';
import { Button, Tag, InputField } from '@jem-open/jem-ui';
import { ArrowLeft, Eye, ClipboardCheck, Clock, FileText, Loader2 } from 'lucide-react';

import apiResponseHandler from '../../../utils/apiResponseHandler';
import {
  FlowSessionDetail,
  FlowSessionOutcome,
  FraudSignals,
  IdentityVerificationListItem,
  ReviewOutcome,
  verificationClient,
  VerificationDocument as VerificationDocumentDetail,
} from '../../../services/verificationClient';
import { severityTagVariant } from './components/identityVerifications/severity';
import { PendingBackend, PendingBackendBanner } from './components/identityVerifications/PendingBackend';
import DocumentReviewModal from './components/identityVerifications/DocumentReviewModal';
import { FRAUD_SIGNAL_DETAILS } from './components/identityVerifications/signals';


const REVIEW_OUTCOMES: ReviewOutcome[] = [
  'confirm_fraud',
  'cleared',
  'accepted_with_override',
  'recapture_required',
  'inconclusive',
  'escalate',
];


function outcomeColor(outcome: FlowSessionOutcome): 'success' | 'failed' | 'pending' {
  if (outcome === 'matched') return 'success';
  if (outcome === 'mismatch') return 'failed';
  return 'pending';
}


const SessionDetailPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const history = useHistory();

  const [session, setSession] = useState<FlowSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // Reveal state — `docId → decrypted image as a data: URL`. Set after a
  // successful POST /admin/documents/<id>/download/ (which writes a
  // DocumentAccessLog row server-side). Held only for the lifetime of this
  // page render, so a refresh re-triggers the reveal flow and another audit
  // row — which is the intended behaviour.
  const [revealedImages, setRevealedImages] = useState<Record<string, string>>({});
  const [showReasonModal, setShowReasonModal] = useState<string | null>(null);
  const [revealing, setRevealing] = useState(false);
  const [revealError, setRevealError] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [ticketRef, setTicketRef] = useState('');
  const [verdict, setVerdict] = useState<ReviewOutcome | ''>('');
  const [verdictReason, setVerdictReason] = useState('');
  // Which doc is currently being reviewed in the per-document modal. Null
  // when no modal is open. Stored at the page level so the submit handler
  // can patch the doc back into the session bundle in place.
  const [reviewingDocId, setReviewingDocId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    verificationClient
      .getFlowSession(sessionId)
      .then((data) => {
        if (cancelled) return;
        setSession(data);
      })
      .catch((err) => {
        apiResponseHandler.handleApiError(err);
        if (cancelled) return;
        setSession(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const confirmReason = useCallback(async () => {
    if (!showReasonModal || !reason.trim() || revealing) return;
    const docId = showReasonModal;
    setRevealing(true);
    setRevealError(null);
    try {
      const dataUrl = await verificationClient.getDocumentImageDataUrl(docId, {
        reason: reason.trim(),
        ticketRef: ticketRef.trim() || undefined,
      });
      setRevealedImages((prev) => ({ ...prev, [docId]: dataUrl }));
      setShowReasonModal(null);
      setReason('');
      setTicketRef('');
    } catch (err) {
      // Surface the server's message in the modal instead of bubbling
      // a generic toast — reviewers need to see "wrong reason format"
      // or "document purged" in context. Falls back to a generic
      // message when the error isn't an axios response.
      const detail = (
        err as { response?: { data?: { detail?: string } } }
      )?.response?.data?.detail;
      setRevealError(detail || 'Failed to reveal image. Please try again.');
    } finally {
      setRevealing(false);
    }
  }, [showReasonModal, reason, ticketRef, revealing]);

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="flex justify-center py-lg">
          <Loader2 className="animate-spin text-secondary-text-label" size={24} />
        </div>
      </Container>
    );
  }

  if (!session) {
    return (
      <Container fluid className="py-4">
        <Button variant="link" leftIcon={<ArrowLeft size={14} />} onClick={() => history.push('/admin/security#identity-verifications')}>
          Back to Identity Verifications
        </Button>
        <div className="mt-md rounded-2xl border border-greyscale-border-default bg-white p-lg text-center text-greyscale-text-caption">
          No data for session {sessionId}.
        </div>
      </Container>
    );
  }

  const { selfie } = session;

  return (
    <Container fluid className="py-4">
      <Button variant="link" leftIcon={<ArrowLeft size={14} />} onClick={() => history.push('/admin/security#identity-verifications')}>
        Back to Identity Verifications
      </Button>

      {/* Header */}
      <div className="mt-sm rounded-2xl border border-greyscale-border-default bg-white p-md">
        <div className="flex flex-wrap items-start justify-between gap-sm">
          <div>
            <h2 className="text-lg font-semibold">Flow session {session.id}</h2>
            <div className="text-xs text-greyscale-text-caption">
              <button
                className="underline text-primary-text-label"
                onClick={() => history.push(`/admin/employees/${session.employee_id}`)}
              >
                {session.employee_name || `Employee #${session.employee_id}`} (#{session.employee_id})
              </button>
              {session.employer_name && <> {' · '} {session.employer_name}</>}
            </div>
            <div className="mt-xxs flex items-center gap-md text-xxs text-greyscale-text-caption">
              {session.started_at && (
                <span><Clock size={10} className="inline" /> Started {new Date(session.started_at).toLocaleString()}</span>
              )}
              {session.duration_seconds !== null && (
                <span>Duration {Math.floor(session.duration_seconds / 60)}m {session.duration_seconds % 60}s</span>
              )}
              {session.bsuid_at_capture && <span>BSUID <code>{session.bsuid_at_capture}</code></span>}
              {session.msisdn_at_capture && <span>MSISDN <code>{session.msisdn_at_capture}</code></span>}
            </div>
          </div>
          <Tag variant={outcomeColor(session.outcome)}>outcome: {session.outcome}</Tag>
        </div>
      </div>

      {/* Consent */}
      {(session.consent_version || session.consent_accepted_at) && (
        <div className="mt-sm rounded-2xl border border-greyscale-border-default bg-white p-md">
          <h4 className="text-sm font-semibold mb-xs flex items-center gap-xxs">
            <FileText size={14} /> Consent
          </h4>
          <div className="grid grid-cols-2 gap-xxs text-xs">
            <div className="text-greyscale-text-caption">Version</div>
            <div className="font-mono">{session.consent_version || '—'}</div>
            <div className="text-greyscale-text-caption">Accepted at</div>
            <div className="font-mono">
              {session.consent_accepted_at ? new Date(session.consent_accepted_at).toLocaleString() : '—'}
            </div>
          </div>
        </div>
      )}

      {/*
        Documents — stacked full-width. A single flow_session_id always
        belongs to ONE Meta Flow (selfie XOR ID capture), so only one of
        these branches actually has content in practice. Skip empty
        sections entirely rather than rendering a "no X captured"
        placeholder — `flowDocs` linearises the present sections so
        `addTopMargin` is correct regardless of which one came first.
      */}
      {(() => {
        const flowDocs: Array<{
          key: string;
          title: string;
          doc: IdentityVerificationListItem;
        }> = [];
        if (selfie) {
          flowDocs.push({ key: selfie.id, title: 'Selfie', doc: selfie });
        }
        session.id_attempts.forEach((attempt, idx) => {
          flowDocs.push({
            key: attempt.id,
            title: `ID document — attempt #${attempt.attempt_no ?? idx + 1}${session.id_attempts.length > 1 ? ` of ${session.id_attempts.length}` : ''}`,
            doc: attempt,
          });
        });
        session.other_documents.forEach((other, idx) => {
          flowDocs.push({
            key: other.id,
            title: `Other document — ${other.document_kind || other.doc_type} #${idx + 1}`,
            doc: other,
          });
        });
        if (flowDocs.length === 0) {
          return (
            <div className="mt-sm rounded-2xl border border-greyscale-border-default bg-white p-md text-xs text-greyscale-text-caption">
              No documents captured for this session.
            </div>
          );
        }
        return (
          <div className="mt-sm">
            {flowDocs.map((entry, idx) => (
              <DocumentPanel
                key={entry.key}
                title={entry.title}
                doc={entry.doc}
                imageDataUrl={revealedImages[entry.doc.id]}
                onRequestView={() => setShowReasonModal(entry.doc.id)}
                onRequestReview={() => setReviewingDocId(entry.doc.id)}
                addTopMargin={idx > 0}
              />
            ))}
          </div>
        );
      })()}

      {/* Credentials written */}
      {session.credentials_written.length > 0 && (
        <div className="mt-sm rounded-2xl border border-greyscale-border-default bg-white p-md">
          <h4 className="text-sm font-semibold mb-xs">Credentials written this session</h4>
          <div className="flex flex-wrap gap-xxs">
            {session.credentials_written.map((c) => (
              <Tag
                key={c.id}
                variant={c.event === 'granted' ? 'success' : c.event === 'revoked' ? 'failed' : 'processing'}
              >
                {c.requirement_code || c.event}
              </Tag>
            ))}
          </div>
        </div>
      )}

      {/* Decision panel — read-only until the SessionReview write
          endpoint lands. Per-document review_outcome already wires to the
          backend (see EmployeeIdentificationDocumentsSection), so the
          option list here matches that enum exactly. */}
      <div className="mt-sm rounded-2xl border border-greyscale-border-default bg-white p-md">
        <h4 className="text-sm font-semibold mb-sm flex items-center gap-xxs">
          Reviewer decision <PendingBackend label="session-level decision endpoint pending" />
        </h4>
        <PendingBackendBanner>
          Per-document review outcomes are wired (`review_outcome` enum on
          VerificationDocument). A session-level decision endpoint
          (`SessionReview` model + side-effects on confirm_fraud) is the
          next backend gap — that's what these buttons will submit when
          enabled.
        </PendingBackendBanner>
        <div className="grid grid-cols-1 gap-sm md:grid-cols-2 opacity-50 pointer-events-none">
          <div className="flex flex-col gap-xxs">
            <span className="text-xs text-greyscale-text-caption">Verdict</span>
            <div className="flex flex-wrap gap-xxs">
              {REVIEW_OUTCOMES.map((v) => (
                <Button
                  key={v}
                  variant={verdict === v ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={() => setVerdict(v)}
                  disabled
                >
                  {v.replace(/_/g, ' ')}
                </Button>
              ))}
            </div>
          </div>
          <InputField
            label="Reason / notes (logged)"
            value={verdictReason}
            placeholder="Required — captured to the audit trail"
            disabled
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVerdictReason(e.target.value)}
          />
        </div>
        <div className="mt-sm flex justify-end gap-xxs">
          <Button variant="secondary" size="sm" disabled>
            Save as draft
          </Button>
          <Button variant="primary" size="sm" disabled>
            Submit decision
          </Button>
        </div>
      </div>

      {/* Reviewer-verdict modal — POST /admin/documents/<id>/review/.
          Lives at the page level so the submit handler can splice the
          updated doc back into the session bundle without a refetch. */}
      {reviewingDocId && (() => {
        const target = findDocInSession(session, reviewingDocId);
        if (!target) return null;
        return (
          <DocumentReviewModal
            doc={target}
            onClose={() => setReviewingDocId(null)}
            onSubmitted={(updated) => {
              setSession(patchDocInSession(session, updated));
            }}
          />
        );
      })()}

      {/* Reason modal */}
      {showReasonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-2xl bg-white p-md">
            <h4 className="text-sm font-semibold mb-xs">Reason for viewing image</h4>
            <p className="text-xs text-greyscale-text-caption mb-sm">
              An entry will be written to DocumentAccessLog with your name, the time, and this
              reason. The image is decrypted server-side and returned inline, then held in memory
              for this view only — closing or refreshing the page requires revealing it again.
            </p>
            <div className="flex flex-col gap-sm">
              <InputField
                label="Reason (required)"
                value={reason}
                placeholder="Reviewing cluster-001 / SAR-018 / etc."
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setReason(e.target.value)}
              />
              <InputField
                label="Ticket ref (optional)"
                value={ticketRef}
                placeholder="INC-1234"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicketRef(e.target.value)}
              />
            </div>
            {revealError && (
              <div className="mt-xs rounded-md border border-error-border-default bg-error-surface-subtle p-xs text-xs text-error-text-label">
                {revealError}
              </div>
            )}
            <div className="mt-sm flex justify-end gap-xxs">
              <Button
                variant="secondary"
                size="sm"
                disabled={revealing}
                onClick={() => {
                  setShowReasonModal(null);
                  setReason('');
                  setTicketRef('');
                  setRevealError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                disabled={!reason.trim() || revealing}
                onClick={confirmReason}
              >
                {revealing ? (
                  <Loader2 className="animate-spin" size={14} />
                ) : (
                  'Reveal image'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};


// Find the doc by id across selfie + id_attempts + other_documents.
// Returns the IdentityVerificationListItem the modal needs, or null when
// nothing in the bundle matches (shouldn't happen — `reviewingDocId`
// always comes from a panel we rendered).
function findDocInSession(
  session: FlowSessionDetail,
  docId: string
): IdentityVerificationListItem | null {
  if (session.selfie?.id === docId) return session.selfie;
  const inAttempts = session.id_attempts.find((d) => d.id === docId);
  if (inAttempts) return inAttempts;
  const inOther = session.other_documents.find((d) => d.id === docId);
  return inOther || null;
}


// Splice a freshly-reviewed doc back into the session bundle. The
// review endpoint returns the full VerificationDocument (heavier shape
// than the queue item); we lift just the fields the queue rendering
// uses so the panel re-renders with the new note / outcome without a
// page refetch.
function patchDocInSession(
  session: FlowSessionDetail,
  updated: VerificationDocumentDetail
): FlowSessionDetail {
  const patched = applyReviewToListItem;
  if (session.selfie?.id === updated.id) {
    return { ...session, selfie: patched(session.selfie, updated) };
  }
  const idIdx = session.id_attempts.findIndex((d) => d.id === updated.id);
  if (idIdx >= 0) {
    const next = [...session.id_attempts];
    next[idIdx] = patched(next[idIdx], updated);
    return { ...session, id_attempts: next };
  }
  const otherIdx = session.other_documents.findIndex((d) => d.id === updated.id);
  if (otherIdx >= 0) {
    const next = [...session.other_documents];
    next[otherIdx] = patched(next[otherIdx], updated);
    return { ...session, other_documents: next };
  }
  return session;
}

function applyReviewToListItem(
  existing: IdentityVerificationListItem,
  updated: VerificationDocumentDetail
): IdentityVerificationListItem {
  return {
    ...existing,
    reviewer_note: updated.reviewer_note,
    review_outcome: updated.review_outcome,
  };
}


function DocumentPanel({
  title,
  doc,
  imageDataUrl,
  onRequestView,
  onRequestReview,
  addTopMargin,
}: {
  title: string;
  doc: IdentityVerificationListItem;
  /** Decrypted image as a `data:` URL, once the reviewer has cleared the
   *  X-Access-Reason gate. Undefined until the reveal modal succeeds. */
  imageDataUrl?: string;
  onRequestView: () => void;
  onRequestReview: () => void;
  addTopMargin?: boolean;
}) {
  return (
  <div className={`rounded-2xl border border-greyscale-border-default bg-white p-md ${addTopMargin ? 'mt-sm' : ''}`}>
    <div className="mb-sm flex items-center justify-between">
      <h4 className="text-sm font-semibold">{title}</h4>
      <div className="flex items-center gap-xxs">
        <Tag
          variant={
            doc.verification_status === 'mismatch'
              ? 'failed'
              : doc.verification_status === 'ocr_passed' || doc.verification_status === 'face_matched'
              ? 'success'
              : 'pending'
          }
        >
          {doc.verification_status}
        </Tag>
        {doc.fraud_severity && (
          <Tag variant={severityTagVariant(doc.fraud_severity)}>{doc.fraud_severity}</Tag>
        )}
        <Button
          variant="link"
          size="xs"
          leftIcon={<ClipboardCheck size={14} />}
          onClick={onRequestReview}
          title="Set reviewer note / verdict"
        >
          review
        </Button>
      </div>
    </div>

    {/* Image — decrypted bytes fetched from the server only after the
        reviewer clears the X-Access-Reason modal; the POST that wrote
        DocumentAccessLog returned them. Held in memory for this render
        only, so a refresh re-triggers the reveal flow (and another audit
        row), which is the intended behaviour. */}
    <div className="mb-sm flex h-48 items-center justify-center overflow-hidden rounded-lg border border-dashed border-greyscale-border-default bg-greyscale-surface-subtle">
      {imageDataUrl ? (
        <img
          src={imageDataUrl}
          alt={`${doc.doc_type} for employee ${doc.employee_id}`}
          className="max-h-full max-w-full object-contain"
        />
      ) : (
        <div className="text-center">
          <div className="text-xs text-greyscale-text-caption mb-xs">
            Image hidden — viewing requires an access reason
          </div>
          <Button variant="secondary" size="sm" leftIcon={<Eye size={14} />} onClick={onRequestView}>
            Reveal image
          </Button>
        </div>
      )}
    </div>

    {/* Metadata */}
    <div className="grid grid-cols-2 gap-xxs text-xs">
      <div className="text-greyscale-text-caption">Method</div>
      <div className="text-right font-mono">{doc.extraction_method || '—'}</div>
      <div className="text-greyscale-text-caption">Captured</div>
      <div className="text-right font-mono">{new Date(doc.captured_at).toLocaleTimeString()}</div>
      {doc.review_outcome && (
        <>
          <div className="text-greyscale-text-caption">Review outcome</div>
          <div className="text-right font-mono">{doc.review_outcome.replace(/_/g, ' ')}</div>
        </>
      )}
    </div>

    {doc.reviewer_note && (
      <div className="mt-xs rounded-md bg-greyscale-surface-subtle p-xs text-xxs italic text-greyscale-text-caption">
        &quot;{doc.reviewer_note}&quot;
      </div>
    )}

    {/* Fraud signals */}
    {doc.fraud_signals && <FraudSignalsBlock signals={doc.fraud_signals} />}
  </div>
  );
}


function FraudSignalsBlock({ signals }: { signals: FraudSignals }) {
  // Drop nulls (no signal computed) and any NA-shaped value (n/a, N/A,
  // NA, na — backend writers aren't consistent). Reviewers want to see
  // what's *known* about this capture, not a wall of unknowns.
  const isNa = (v: unknown) =>
    typeof v === 'string' && ['n/a', 'na', 'unknown', ''].includes(v.toLowerCase().trim());
  const items = FRAUD_SIGNAL_DETAILS
    .map(({ key, label }) => ({ label, value: signals[key] }))
    .filter(({ value }) => value != null && !isNa(value))
    .map(({ label, value }) => [label, String(value)] as [string, string]);

  if (items.length === 0) return null;

  return (
    <div className="mt-sm border-t border-greyscale-border-subtle pt-sm">
      <div className="mb-xxs text-xxs text-greyscale-text-caption uppercase tracking-wide">
        Fraud signals
      </div>
      <div className="grid grid-cols-2 gap-xxs text-xs">
        {items.map(([label, value]) => (
          <React.Fragment key={label}>
            <div className="text-greyscale-text-caption">{label}</div>
            <div className="text-right font-mono">
              {value === 'true' ? <span className="text-error-text-label">true</span> : value}
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

export default SessionDetailPage;
